import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { contractService, Contract } from '../services/contractService';
import type { ContractStatus, ContractSignWithStampRequest } from '../services/contractService';
import { Button } from '../components/common/Button';
import { StatusBadge } from '../components/common/StatusBadge';
import { ncaLayerService, SignatureResult } from '../services/ncaLayerService';
import { useAuth } from '../contexts/AuthContext';

const ContractSigningPage: React.FC = () => {
    const { contractId } = useParams<{ contractId: string }>();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [contract, setContract] = useState<Contract | null>(null);
    const [loading, setLoading] = useState(true);
    const [signing, setSigning] = useState(false);
    const [certificates, setCertificates] = useState<any[]>([]);
    const [selectedCertificate, setSelectedCertificate] = useState<string>('');
    const [certificatePassword, setCertificatePassword] = useState<string>('');
    const [ncaLayerStatus, setNcaLayerStatus] = useState<'checking' | 'available' | 'unavailable'>('checking');
    const [showCertificateSelection, setShowCertificateSelection] = useState(false);

    useEffect(() => {
        if (contractId) {
            loadContract();
            checkNCALayer();
        }
    }, [contractId]);

    const checkNCALayer = async () => {
        try {
            setNcaLayerStatus('checking');
            const isAvailable = await ncaLayerService.isAvailable();

            if (isAvailable) {
                setNcaLayerStatus('available');
                await loadCertificates();
            } else {
                setNcaLayerStatus('unavailable');
            }
        } catch (error) {
            console.error('Ошибка проверки NCALayer:', error);
            setNcaLayerStatus('unavailable');
        }
    };

    const loadCertificates = async () => {
        try {
            const certs = await ncaLayerService.getCertificates();
            setCertificates(certs);
            if (certs.length > 0) {
                // Автоматически выбираем PKCS12 если доступен
                const pkcs12Cert = certs.find(cert => cert.path === 'PKCS12');
                setSelectedCertificate(pkcs12Cert?.path || certs[0].path);
            }
        } catch (error) {
            console.error('Ошибка загрузки сертификатов:', error);
            toast.error('Ошибка загрузки сертификатов');
        }
    };

    const loadContract = async () => {
        try {
            setLoading(true);

            if (!contractId) {
                toast.error('ID договора не указан');
                navigate('/contracts');
                return;
            }

            const response = await contractService.getContract(parseInt(contractId));

            if (response.success && response.data) {
                setContract(response.data);

                // Проверяем можно ли подписать договор
                if (!contractService.canSignContract(response.data.status)) {
                    toast.error('Этот договор нельзя подписать');
                    navigate('/contracts');
                }
            } else {
                throw new Error(response.message || 'Договор не найден');
            }

        } catch (error: any) {
            console.error('Ошибка загрузки договора:', error);
            toast.error(error.message || 'Ошибка загрузки договора');
            navigate('/contracts');
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadContract = async () => {
        if (!contract) return;

        try {
            const blob = await contractService.downloadContract(contract.id);

            // Создаем ссылку для скачивания
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${contract.contractNumber}.docx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            toast.success('Договор скачан');

        } catch (error: any) {
            console.error('Ошибка скачивания договора:', error);
            toast.error(error.message || 'Ошибка скачивания договора');
        }
    };

    const handleSignContract = async () => {
        if (!contract) return;

        try {
            setSigning(true);

            // Проверяем доступность NCALayer
            if (ncaLayerStatus !== 'available') {
                toast.error('NCALayer недоступен. Установите NCALayer и запустите его.');
                return;
            }

            // Используем выбранный сертификат или PKCS12 по умолчанию
            const certPath = selectedCertificate || 'PKCS12';

            // Подготавливаем данные для подписания
            const contractData = JSON.stringify({
                contractId: contract.id,
                contractNumber: contract.contractNumber,
                companyBin: contract.companyBin,
                companyName: contract.companyName,
                timestamp: new Date().toISOString()
            });

            toast.loading('Подписание договора...', { id: 'signing' });

            // Подписываем данные через NCALayer
            const signatureResult: SignatureResult = await ncaLayerService.signData(
                contractData,
                certPath,
                certificatePassword
            );

            // Отправляем подпись на сервер с компактным штампом покупателя
            const signResponse = await contractService.signContractCustomer(contract.id, {
                signatureData: signatureResult.signature,
                certificateData: signatureResult.certificate,
                signerInfo: JSON.stringify(signatureResult.signerInfo),
                signerName: signatureResult.signerInfo.commonName,
                organizationName: signatureResult.signerInfo.organization,
                signDate: new Date().toLocaleString('ru-RU')
            });

            if (signResponse.success) {
                toast.success('Договор успешно подписан!', { id: 'signing' });
                navigate('/contracts');
            } else {
                throw new Error(signResponse.message || 'Ошибка сохранения подписи');
            }

        } catch (error: any) {
            console.error('Ошибка подписания договора:', error);
            toast.error(error.message || 'Ошибка подписания договора', { id: 'signing' });
        } finally {
            setSigning(false);
        }
    };

    const handleCancelSigning = () => {
        navigate('/contracts');
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Загрузка договора...</p>
                </div>
            </div>
        );
    }

    if (!contract) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">Договор не найден</h2>
                    <Button
                        onClick={() => navigate('/contracts')}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
                    >
                        Вернуться к списку договоров
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Заголовок */}
                <div className="mb-8">
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900">Подписание договора</h1>
                            <p className="mt-2 text-gray-600">
                                Договор поставки № {contract.contractNumber}
                            </p>
                        </div>
                        <StatusBadge
                            status={contract.status}
                            text={contractService.getStatusDisplayName(contract.status)}
                            className={contractService.getStatusColor(contract.status)}
                        />
                    </div>
                </div>

                {/* Информация о договоре */}
                <div className="bg-white shadow rounded-lg mb-8">
                    <div className="px-6 py-4 border-b border-gray-200">
                        <h3 className="text-lg font-medium text-gray-900">Информация о договоре</h3>
                    </div>
                    <div className="px-6 py-4">
                        <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                            <div>
                                <dt className="text-sm font-medium text-gray-500">Номер договора</dt>
                                <dd className="mt-1 text-sm text-gray-900">{contract.contractNumber}</dd>
                            </div>
                            <div>
                                <dt className="text-sm font-medium text-gray-500">Компания</dt>
                                <dd className="mt-1 text-sm text-gray-900">{contract.companyName}</dd>
                            </div>
                            <div>
                                <dt className="text-sm font-medium text-gray-500">БИН компании</dt>
                                <dd className="mt-1 text-sm text-gray-900">{contract.companyBin}</dd>
                            </div>
                            <div>
                                <dt className="text-sm font-medium text-gray-500">Дата создания</dt>
                                <dd className="mt-1 text-sm text-gray-900">
                                    {new Date(contract.createdAt).toLocaleDateString('ru-RU', {
                                        year: 'numeric',
                                        month: 'long',
                                        day: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit'
                                    })}
                                </dd>
                            </div>
                            <div>
                                <dt className="text-sm font-medium text-gray-500">Версия шаблона</dt>
                                <dd className="mt-1 text-sm text-gray-900">v1.0</dd>
                            </div>
                            <div>
                                <dt className="text-sm font-medium text-gray-500">Статус</dt>
                                <dd className="mt-1">
                                    <StatusBadge
                                        status={contract.status}
                                        text={contractService.getStatusDisplayName(contract.status)}
                                        className={contractService.getStatusColor(contract.status)}
                                    />
                                </dd>
                            </div>
                        </dl>
                    </div>
                </div>

                {/* Предварительный просмотр договора */}
                <div className="bg-white shadow rounded-lg mb-8">
                    <div className="px-6 py-4 border-b border-gray-200">
                        <h3 className="text-lg font-medium text-gray-900">Предварительный просмотр</h3>
                    </div>
                    <div className="px-6 py-4">
                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                            <div className="text-gray-400 mb-4">
                                <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                            </div>
                            <h4 className="text-lg font-medium text-gray-900 mb-2">Договор готов к подписанию</h4>
                            <p className="text-gray-500 mb-4">
                                Договор поставки сгенерирован и готов к подписанию электронной подписью
                            </p>
                            <Button
                                onClick={handleDownloadContract}
                                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
                            >
                                Скачать договор для просмотра
                            </Button>
                        </div>
                    </div>
                </div>

                {/* Выбор сертификата для подписания */}
                <div className="bg-white shadow rounded-lg mb-8">
                    <div className="px-6 py-4 border-b border-gray-200">
                        <h3 className="text-lg font-medium text-gray-900">Электронная подпись</h3>
                    </div>
                    <div className="px-6 py-4">
                        {/* Статус NCALayer */}
                        <div className="mb-6">
                            <div className="flex items-center">
                                <div className={`w-3 h-3 rounded-full mr-3 ${ncaLayerStatus === 'available' ? 'bg-green-500' :
                                    ncaLayerStatus === 'unavailable' ? 'bg-red-500' : 'bg-yellow-500'
                                    }`}></div>
                                <span className="text-sm font-medium text-gray-700">
                                    NCALayer: {
                                        ncaLayerStatus === 'available' ? 'Доступен' :
                                            ncaLayerStatus === 'unavailable' ? 'Недоступен' : 'Проверка...'
                                    }
                                </span>
                            </div>
                            {ncaLayerStatus === 'unavailable' && (
                                <div className="mt-2">
                                    <p className="text-sm text-red-600 mb-2">
                                        Установите и запустите NCALayer для подписания документов
                                    </p>
                                    <button
                                        onClick={checkNCALayer}
                                        className="px-3 py-1.5 bg-red-600 text-white rounded hover:bg-red-700 transition-colors text-sm"
                                    >
                                        Проверить снова
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Выбор сертификата */}
                        {ncaLayerStatus === 'available' && certificates.length > 0 && (
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Выберите сертификат
                                    </label>
                                    <select
                                        value={selectedCertificate}
                                        onChange={(e) => setSelectedCertificate(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    >
                                        {certificates.map((cert, index) => (
                                            <option key={index} value={cert.path}>
                                                {cert.alias} - {cert.subject}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Пароль сертификата (если требуется)
                                    </label>
                                    <input
                                        type="password"
                                        value={certificatePassword}
                                        onChange={(e) => setCertificatePassword(e.target.value)}
                                        placeholder="Введите пароль сертификата"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>
                            </div>
                        )}


                    </div>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
                    <div className="flex">
                        <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <div className="ml-3">
                            <h3 className="text-sm font-medium text-blue-800">
                                Инструкции по подписанию
                            </h3>
                            <div className="mt-2 text-sm text-blue-700">
                                <ul className="list-disc list-inside space-y-1">
                                    <li>Убедитесь, что у вас установлен NCA Layer</li>
                                    <li>Проверьте действительность вашего сертификата ЭЦП</li>
                                    <li>Скачайте договор и внимательно ознакомьтесь с его содержанием</li>
                                    <li>Нажмите "Подписать договор" для начала процесса подписания</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Действия */}
                <div className="flex justify-between">
                    <Button
                        onClick={handleCancelSigning}
                        className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-3 rounded-lg font-medium"
                    >
                        Отменить
                    </Button>

                    <Button
                        onClick={handleSignContract}
                        disabled={signing || ncaLayerStatus !== 'available'}
                        className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-medium disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                        {signing ? 'Подписание...' : 'Подписать договор'}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default ContractSigningPage;